/* eslint-disable */
import * as React from 'react';
import {
  Badge, Box, Button, Flex, HStack, Icon, IconButton, Link, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdBarChart, MdChevronLeft, MdChevronRight, MdContentCopy, MdOpenInNew, MdRefresh } from 'react-icons/md';
import {
  ColumnFiltersState,
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';

type RowObj = {
  id: number;
  Titulo?: string | null;
  Acceso: string;
  FechaI?: string | null;
  FechaF?: string | null;
  createdAt?: string | null;
  Bloqueada?: boolean;
};

type SurveyResults = {
  id: number;
  titulo?: string;
  totalRespuestas: number;
  preguntas: Array<{
    id: number;
    tipo?: string;
    descripcion?: string;
    opciones?: string;
    totalRespuestas: number;
    resumen: Record<string, number>;
  }>;
};

export default function SearchTableOrders(props: { tableData: RowObj[] }) {
  const { tableData } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [data, setData] = React.useState<RowObj[]>(() => [...tableData]);
  const [results, setResults] = React.useState<SurveyResults | null>(null);
  const [loadingResultsId, setLoadingResultsId] = React.useState<number | null>(null);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 8 });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const refreshTable = async () => {
    const response = await fetch(process.env.REACT_APP_URL_SERVER + '/encuestas', { credentials: 'include' }).then((res) => res.json()).catch(() => null);
    if (response) setData(response);
  };

  const copyLink = async (access: string) => {
    const link = `${window.location.origin}/encuesta/${access}`;
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: 'Enlace copiado', description: link, status: 'success', duration: 2500 });
    } catch {
      toast({ title: 'No fue posible copiar el enlace', status: 'error', duration: 2500 });
    }
  };

  const openResults = async (id: number) => {
    setLoadingResultsId(id);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL_SERVER}/encuesta_resultados/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const payload = await response.json();
      setResults(payload);
      onOpen();
    } catch (error) {
      toast({
        title: 'No fue posible cargar resultados',
        description: error instanceof Error ? error.message : 'Ocurrio un error al consultar la encuesta.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingResultsId(null);
    }
  };

  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    columnHelper.accessor('Titulo', {
      id: 'titulo',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ENCUESTA</Text>,
      cell: (info) => (
        <Box>
          <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || 'Encuesta sin título'}</Text>
          <Text color="gray.400" fontSize="xs">ID #{info.row.original.id}</Text>
        </Box>
      ),
    }),
    columnHelper.accessor('FechaI', {
      id: 'periodo',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">PERIODO</Text>,
      cell: (info) => {
        const start = info.getValue();
        const end = info.row.original.FechaF;
        return (
          <Text color={textColor} fontSize="sm" fontWeight="500">
            {start ? new Date(start).toLocaleDateString('es-MX') : 'Sin inicio'} {' - '}
            {end ? new Date(end).toLocaleDateString('es-MX') : 'Sin cierre'}
          </Text>
        );
      },
    }),
    columnHelper.accessor('Acceso', {
      id: 'acceso',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ENLACE PUBLICO</Text>,
      cell: (info) => {
        const link = `${window.location.origin}/encuesta/${info.getValue()}`;
        return (
          <Link href={link} isExternal color={brandColor} fontSize="sm" fontWeight="600">
            {link}
          </Link>
        );
      },
    }),
    columnHelper.accessor('Bloqueada', {
      id: 'estatus',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ESTATUS</Text>,
      cell: (info) => (
        <Badge colorScheme={info.getValue() ? 'red' : 'green'} borderRadius="full" px="3" py="1">
          {info.getValue() ? 'Bloqueada' : 'Activa'}
        </Badge>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ACCIONES</Text>,
      cell: (info) => (
        <HStack spacing="4px">
          <Tooltip label="Ver resultados">
            <IconButton aria-label="survey-results" size="sm" variant="ghost" icon={<Icon as={MdBarChart} h="18px" w="18px" />} isLoading={loadingResultsId === info.row.original.id} onClick={() => openResults(info.row.original.id)} />
          </Tooltip>
          <Tooltip label="Copiar enlace">
            <IconButton aria-label="copy-link" size="sm" variant="ghost" icon={<Icon as={MdContentCopy} h="18px" w="18px" />} onClick={() => copyLink(info.row.original.Acceso)} />
          </Tooltip>
          <Tooltip label="Abrir encuesta">
            <IconButton aria-label="open-link" size="sm" variant="ghost" icon={<Icon as={MdOpenInNew} h="18px" w="18px" />} onClick={() => window.open(`${window.location.origin}/encuesta/${info.row.original.Acceso}`, '_blank')} />
          </Tooltip>
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { columnFilters, globalFilter, pagination },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const createPages = (count: number) => Array.from({ length: count }, (_, i) => i + 1);

  return (
    <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex align="center" justify="space-between" w="100%" px="22px" mb="24px" flexWrap="wrap" gap="10px">
        <DebouncedInput value={globalFilter ?? ''} onChange={(value) => setGlobalFilter(String(value))} placeholder="Buscar encuesta..." />
        <HStack spacing="8px">
          <Tooltip label="Actualizar">
            <IconButton aria-label="refresh" size="md" variant="outline" icon={<Icon as={MdRefresh} h="18px" w="18px" />} onClick={refreshTable} />
          </Tooltip>
          <Button variant="brand" fontSize="sm" fontWeight="600" leftIcon={<Icon as={MdAdd} />} onClick={() => navigate('/admin/main/ERP/Candidatos/NuevaEncuesta')}>
            Nueva Encuesta
          </Button>
        </HStack>
      </Flex>

      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th pe="10px" borderColor={borderColor} key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <Flex justify="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.length === 0 ? (
            <Tr>
              <Td colSpan={columns.length} textAlign="center" py="40px" color="gray.400">Sin encuestas registradas</Td>
            </Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Tr key={row.id} _hover={{ bg: rowHover }}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} fontSize={{ sm: '14px' }} minW={{ sm: '120px', md: 'auto' }} borderColor={borderColor} py="12px">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{results?.titulo || 'Resultados de encuesta'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="gray.500" fontSize="sm" mb="18px">
              Respuestas registradas: {results?.totalRespuestas ?? 0}
            </Text>
            <Stack spacing="16px">
              {results?.preguntas?.map((pregunta, index) => (
                <Box key={pregunta.id} border="1px solid" borderColor={borderColor} borderRadius="18px" p="16px">
                  <Text color={textColor} fontWeight="800" mb="10px">
                    {index + 1}. {pregunta.descripcion || 'Pregunta sin descripcion'}
                  </Text>
                  <Text color="gray.500" fontSize="xs" mb="12px">
                    Total de respuestas: {pregunta.totalRespuestas}
                  </Text>
                  <Stack spacing="8px">
                    {Object.keys(pregunta.resumen || {}).length === 0 ? (
                      <Text color="gray.400" fontSize="sm">Aun no hay respuestas.</Text>
                    ) : (
                      Object.entries(pregunta.resumen).map(([label, value]) => (
                        <Flex key={label} align="center" justify="space-between" gap="12px">
                          <Text color={textColor} fontSize="sm">{label}</Text>
                          <Badge colorScheme="blue" borderRadius="full" px="3" py="1">{value}</Badge>
                        </Flex>
                      ))
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px">
        <Text fontSize="sm" color="gray.500" mb={{ sm: '24px', md: '0px' }}>
          {data.length === 0 ? 'Sin registros' : `${pageSize * pageIndex + 1}-${Math.min(pageSize * (pageIndex + 1), data.length)} de ${data.length}`}
        </Text>
        <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
          <Button variant="no-effects" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanPreviousPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}>
            <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
          </Button>
          {createPages(table.getPageCount()).map((page) => (
            <Button key={page} variant="no-effects" onClick={() => table.setPageIndex(page - 1)} w="40px" h="40px" borderRadius="50%" bg={page === pageIndex + 1 ? brandColor : 'transparent'} border={page === pageIndex + 1 ? 'none' : '1px solid lightgray'} _hover={page === pageIndex + 1 ? { opacity: '0.7' } : { bg: 'whiteAlpha.100' }}>
              <Text fontSize="sm" color={page === pageIndex + 1 ? '#fff' : textColor}>{page}</Text>
            </Button>
          ))}
          <Button variant="no-effects" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanNextPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}>
            <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
          </Button>
        </Stack>
      </Flex>
    </Flex>
  );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }: { value: string | number; onChange: (value: string | number) => void; debounce?: number } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => onChange(value), debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return <SearchBar {...props} value={value} onChange={(e: any) => setValue(e.target.value)} h="44px" w={{ lg: '390px' }} borderRadius="16px" />;
}
